import React from 'react'
import { take } from 'lodash'
import { useParams } from 'react-router'
import { DateRange } from '../../../lib/types/DateRange'
import PolarAreaChart from '../../Charts/PolarAreaChart'
import { useGetDisplayCampaignContentReports } from '../../../api/dealership/campaigns/reports/useGetDisplayCampaignContentReports'
import HorizontalBarChart from '../../Charts/HorizontalBarChart'
import { useGetDisplayCampaignReports } from '../../../api/dealership/campaigns/reports/useGetDisplayCampaignReports'
import { useGetDisplayCampaignById } from '../../../api/dealership/campaigns/useGetDisplayCampaignById'

type Props = {
  filters: DateRange
}

const ContentMetrics = ({ filters }: Props) => {
  const { campaign_id } = useParams()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id })

  if (['AZD', 'AZV'].includes(campaign.product_code))
    return <AmazonContent filters={filters} />
  else return <TTDContent filters={filters} />
}

const TTDContent = ({ filters }: Props) => {
  const { campaign_id } = useParams()
  const {
    data: content,
    isLoading,
    isPreviousData,
  } = useGetDisplayCampaignContentReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  return (
    <>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Top networks</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                take(
                  content?.networks?.map((data) => {
                    return {
                      label: data.content_network,
                      value: data.impressions,
                    }
                  }),
                  5,
                ) ?? []
              }
              isPreviousData={isPreviousData}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Top genres</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              data={
                take(
                  content?.genres?.map((data) => {
                    return {
                      label: data.genre,
                      value: data.impressions,
                    }
                  }),
                  5,
                ) ?? []
              }
              isPreviousData={isPreviousData}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Top channels</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <HorizontalBarChart
              data={
                take(
                  content?.channels?.map((data) => {
                    return {
                      label: data.channel,
                      value: data.impressions,
                    }
                  }),
                  5,
                ) ?? []
              }
              isPreviousData={isPreviousData}
              isLoading={isLoading}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Top shows</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <HorizontalBarChart
              data={
                take(
                  content?.shows?.map((data) => {
                    return {
                      label: data.show,
                      value: data.impressions,
                    }
                  }),
                  5,
                ) ?? []
              }
              isPreviousData={isPreviousData}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </>
  )
}

const AmazonContent = ({ filters }: Props) => {
  const { campaign_id } = useParams()
  const {
    data: { geoInsightsCityData } = { geoInsightsCityData: [] },
    data: { amazonPublishers } = { amazonPublishers: [] },
    isLoading,
    isPreviousData,
  } = useGetDisplayCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  return (
    <>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Impressions by city</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <PolarAreaChart
              isPreviousData={isPreviousData}
              isLoading={isLoading}
              data={
                take(
                  geoInsightsCityData
                    ?.filter((d) => d.type === 'city')
                    ?.map((data: { location_name: string; impressions: number }) => {
                      return {
                        label: data.location_name,
                        value: data.impressions,
                      }
                    }),
                  5,
                ) ?? []
              }
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span>Premium publishers</span>
          </div>
          <div className={'h-[270px] p-2'}>
            <HorizontalBarChart
              isPreviousData={isPreviousData}
              isLoading={isLoading}
              data={
                take(
                  amazonPublishers?.map(({ site, impressions }) => {
                    return {
                      label: site,
                      value: impressions,
                    }
                  }),
                  5,
                ) ?? []
              }
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default ContentMetrics
